import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import { SITE_NAME } from '../config/site';

export default function AboutPage() {
  const title = `About ${SITE_NAME} | Mission & Science`;
  const description = `Learn why ${SITE_NAME} exists, our mission for sustainable weight loss in India without calorie tracking, and the science behind habit-based nutrition.`;

  return (
    <>
      <SeoMeta title={title} description={description} path="/about" ogType="website" />

      <main className="static-page">
        <header className="static-page-hero container">
          <h1>About {SITE_NAME}</h1>
          <p className="static-page-lead">
            We build simple, evidence-informed guidance for people who want results without living inside a calorie-tracking
            app—especially with Indian home food and busy real-world schedules.
          </p>
        </header>

        <article className="container static-page-body">
          <section className="static-section" aria-labelledby="mission-heading">
            <h2 id="mission-heading">Mission Statement</h2>
            <p>
              {SITE_NAME} exists to make sustainable weight loss accessible to anyone who has failed “perfect” diet plans.
              Millions of people in India know what to eat at home—roti, dal, rice, eggs, paneer—but crumble when apps demand
              daily logging, strict rules, and all-or-nothing streaks. Our mission is to replace that friction with{' '}
              <strong>clear daily structure</strong>, <strong>habit-based consistency</strong>, and support for real kitchens,
              not theoretical meal plans.
            </p>
            <p>
              We believe trust comes from honesty: weight change still depends on energy balance over time, but you should
              not have to become an accountant for every meal. {SITE_NAME} focuses on what you can repeat for months—protein at
              meals, sensible portions, vegetables, sleep, and movement—not on shaming you for missing one day.
            </p>
          </section>

          <section className="static-section" aria-labelledby="science-heading">
            <h2 id="science-heading">The Science Behind the App</h2>
            <p>
              {SITE_NAME} is grounded in widely accepted principles from nutrition and behaviour science: sustained fat loss
              generally requires a moderate energy deficit over weeks, adequate protein helps protect muscle and improve
              satiety, fibre-rich foods support fullness and metabolic health, and sleep and stress influence appetite and
              adherence. We translate these ideas into <strong>practical defaults for Indian diets</strong>—not generic Western
              meal replacements—so guidance matches the food you already cook.
            </p>
            <p>
              Behaviourally, the evidence favours plans people can stick to. Calorie counting works when maintained, but
              adherence often drops when life interrupts logging. Habit design, meal templates, and forgiving routines (for
              example, resuming after a missed day instead of “starting over”) align with what we know about long-term
              behaviour change. {SITE_NAME} is built as a coaching and structure layer—not a substitute for medical advice:
              if you have a clinical condition, you should work with a qualified professional alongside any app.
            </p>
          </section>

          <footer className="static-page-foot">
            <p>
              <Link to="/contact">Contact {SITE_NAME}</Link>
              {' · '}
              <Link to="/">Back to home</Link>
            </p>
          </footer>
        </article>
      </main>
    </>
  );
}
